import { FC } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LinearProgress, makeStyles, Typography } from 'beam'
import { AuthenticatedUserResult, useAuthenticatedUser } from './useAuthenticatedUser'
import { useAppSelector } from '../root.reducer'
import { ApplicationPage } from '../App/app.api'
import { WfmConfigC1 } from '../App/LegacyLogin/login.api'
import { ORDERED_DEFAULT_PATHS } from '../page.constants'
import { useIsPathAvailable } from '../FeatureFlags'
import { RedirectToExternal } from '../Common/RedirectToExternal'
import { ROUTE_PATHS } from '../routes'

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(4),
  },
}))

const getDefaultPath = (user: AuthenticatedUserResult, isPathAvailable: (path: string) => boolean) => {
  return ORDERED_DEFAULT_PATHS.find(path => isPathAvailable(path) && user.canAccessRoute(path)) || ROUTE_PATHS.WELCOME_PAGE
}

export enum LandingPageResolvability {
  UNRESOLVABLE,
  AWAITING_STATE,
  RESOLVABLE,
}

interface LandingPageResolver {
  token: string,
  resolvability: LandingPageResolvability,
  value: string | undefined,
  isAvailable: (resolvedUrl: string) => boolean,
  isPermitted: (resolvedUrl: string) => boolean,
}

const CLASSIC_PATH = '/classic'

export const c1RelativeUrlToSpaRoute = (baseUrl: string, url: string) => {
  if (!url.startsWith(baseUrl)) {
    throw new Error(`Url ${url} does not start with base url ${baseUrl}`)
  }
  let route = url.substring(baseUrl.length)
  if (route.startsWith(CLASSIC_PATH)) {  // use react-router to handle classic routes
    route = route.substring(CLASSIC_PATH.length)
  }
  if (route.startsWith('/#')) {
    route = route.substring(2)
  }
  return route
}

const getC1Resolver = (
  c1BaseUrl: string,
  isPathAvailable: (path: string) => boolean,
  isPathPermitted: (path: string) => boolean,
): LandingPageResolver => {
  return {
    token: '{{C1}}',
    resolvability: LandingPageResolvability.RESOLVABLE,
    value: c1BaseUrl,
    isAvailable: (resolvedUrl: string) => isPathAvailable(c1RelativeUrlToSpaRoute(c1BaseUrl, resolvedUrl)),
    isPermitted: (resolvedUrl: string) => isPathPermitted(c1RelativeUrlToSpaRoute(c1BaseUrl, resolvedUrl)),
  }
}

const getWfmLandingPageResolver = (
  wasAuthFr: boolean,
  wfmConfig: WfmConfigC1 | undefined,
): LandingPageResolver => {
  let resolvability: LandingPageResolvability
  // we need to wait for the wfmConfigC1 to be available but if there's an error or we've not used FR auth, we can't resolve
  if (!wasAuthFr || !!wfmConfig && wfmConfig.error) {
    resolvability = LandingPageResolvability.UNRESOLVABLE
  } else if (!wfmConfig || wfmConfig?.wfmBaseUrl === '') {
    resolvability = LandingPageResolvability.AWAITING_STATE
  } else {
    resolvability = LandingPageResolvability.RESOLVABLE
  }
  return {
    token: '{{WFM}}',
    resolvability,
    value: wfmConfig?.wfmBaseUrl,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we expect callers to supply a resolvedUrl
    isAvailable: (resolvedUrl: string) => true,
    isPermitted: (resolvedUrl: string) => {
      if (!wfmConfig?.navItems) {
        return false
      }
      return Object.values(wfmConfig.navItems).some(
        item => resolvedUrl.endsWith(item.url.substring(2)) && item.isPermitted)  // remove the leading '..' from the url
    },
  }
}

const externalLandingPageResolver: LandingPageResolver = {
  token: 'http',
  resolvability: LandingPageResolvability.RESOLVABLE,
  value: 'http',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- conform to interface
  isAvailable: (resolvedUrl: string) => true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- conform to interface
  isPermitted: (resolvedUrl: string) => true,
}

const resolveLandingPage = (landingPage: ApplicationPage, resolver: LandingPageResolver): string => {
  if (!landingPage.tokenizedUrl.startsWith(resolver.token)) {
    throw new Error(`tokenizedUrl expected to start with ${resolver.token}: ${landingPage.tokenizedUrl}`)
  }
  return landingPage.tokenizedUrl.replace(resolver.token, resolver.value || '')
}

function getResolver(landingPage: ApplicationPage, resolvers: LandingPageResolver[]): LandingPageResolver {
  const resolver = resolvers.find(
    resolver => landingPage.tokenizedUrl.startsWith(resolver.token))

  if (!resolver) {
    throw new Error(`No resolver found for token ${landingPage.tokenizedUrl}`)
  }
  return resolver
}

const getDefaultRoute = (
  authenticatedUser: AuthenticatedUserResult,
  isPathAvailable: (path: string) => boolean,
): string => {
  return '/#' + getDefaultPath(authenticatedUser, isPathAvailable)
}

export function getRootRoute(
  c1BaseUrl: string,
  wasAuthFr: boolean,
  wfmConfig: WfmConfigC1 | undefined,
  authenticatedUser: AuthenticatedUserResult,
  isPathAvailable: (path: string) => boolean,
  landingPage: ApplicationPage | null,
): [string | null, LandingPageResolvability] {
  if (!landingPage) {
    return [getDefaultRoute(authenticatedUser, isPathAvailable), LandingPageResolvability.UNRESOLVABLE]
  }

  const resolvers = [
    getC1Resolver(c1BaseUrl, isPathAvailable, authenticatedUser.canAccessRoute),
    getWfmLandingPageResolver(wasAuthFr, wfmConfig),
    externalLandingPageResolver,
  ]

  const resolver = getResolver(landingPage, resolvers)

  if (resolver.resolvability === LandingPageResolvability.AWAITING_STATE) {
    return [null, LandingPageResolvability.AWAITING_STATE]
  }

  if (resolver.resolvability === LandingPageResolvability.UNRESOLVABLE) {
    return [getDefaultRoute(authenticatedUser, isPathAvailable), LandingPageResolvability.UNRESOLVABLE]
  }

  const route = resolveLandingPage(landingPage, resolver)
  if (!resolver.isAvailable(route) || !resolver.isPermitted(route)) {
    return [getDefaultRoute(authenticatedUser, isPathAvailable), resolver.resolvability]
  }
  return [route, resolver.resolvability]
}

const RootRoute: FC = () => {
  const location = useLocation()
  const isPathAvailable = useIsPathAvailable()
  const classes = useStyles()
  const authenticatedUser = useAuthenticatedUser()
  const landingPage = useAppSelector(state => state.app.landingPage)
  const wfmMenuItems = useAppSelector(state => state.app.wfmMenuItems ?? undefined)
  const wasAuthFR = useAppSelector(state => state.app.wasAuthFR)
  const { t } = useTranslation()
  const c1BaseUrl = location.pathname.replace(/\/$/, '')

  const [url, landingPageResolvability] = getRootRoute(c1BaseUrl, wasAuthFR, wfmMenuItems, authenticatedUser, isPathAvailable, landingPage)
  if (landingPageResolvability === LandingPageResolvability.AWAITING_STATE) {
    return <LinearProgress color="secondary"/>
  }

  if (url?.startsWith('http')) {
    return <RedirectToExternal url={url} title={landingPage?.title} />
  }

  if (url === null) {
    return <div className={classes.root}><Typography variant="h4">{t('noUserPermissions')}</Typography></div>
  }

  return <Redirect to={c1RelativeUrlToSpaRoute(c1BaseUrl, url)} />
}

export default RootRoute
